// How it works: the yard path. Switches throw and a dithered carriage travels into your account.
// Horizontal and scroll-pinned on wide screens, vertical on mobile, finished state under reduced motion.
import { clamp01, onFrame, state, viewportWatcher } from "./core.ts"

interface Track {
  readonly svg: SVGSVGElement
  readonly main: SVGPathElement
  readonly len: number
  readonly trail: SVGPathElement
  readonly car: SVGGElement
  readonly stops: ReadonlyArray<{ readonly el: Element; readonly at: number }>
  readonly vert: boolean
}

/** The carriage: a 6×3 block of dither cells whose sizes jitter like the dot fields. */
const carriage = (): string => {
  let html = ""
  for (let j = 0; j < 3; j++) {
    for (let i = 0; i < 6; i++) {
      const s = 4 + ((i * 7 + j * 3) % 3)
      html += `<rect x="${i * 7 - 21 + (7 - s) / 2}" y="${j * 7 - 10.5 + (7 - s) / 2}" width="${s}" height="${s}"/>`
    }
  }
  return html
}

export function initYardPath(): void {
  const sec = document.querySelector<HTMLElement>(".yardpath")
  if (!sec) return
  const tracks: Track[] = []
  for (const svg of sec.querySelectorAll<SVGSVGElement>(".yp-svg")) {
    const main = svg.querySelector<SVGPathElement>(".main")
    const trail = svg.querySelector<SVGPathElement>(".trail")
    const car = svg.querySelector<SVGGElement>(".car")
    if (!main || !trail || !car) continue
    const len = main.getTotalLength()
    trail.style.strokeDasharray = String(len)
    trail.style.strokeDashoffset = String(len)
    for (const s of svg.querySelectorAll<SVGGeometryElement>(".sid-trail")) {
      s.style.setProperty("--len", String(s.getTotalLength()))
    }
    const stops = [...svg.querySelectorAll<SVGElement>(".stop")].map((el) => ({
      el,
      at: Number(el.dataset.at),
    }))
    car.innerHTML = carriage()
    tracks.push({ svg, main, len, trail, car, stops, vert: svg.classList.contains("yp-v") })
  }
  let lp = -1
  const moved = viewportWatcher()
  onFrame(() => {
    // The carriage only moves with scroll, so frames where nothing scrolled have nothing to do.
    if (!moved() && lp >= 0) return
    // Only one of the two tracks (horizontal or vertical) is displayed at a time.
    const o = tracks.find((o) => o.svg.getBoundingClientRect().width > 0)
    if (!o) return
    let p: number
    if (state.reduced) p = 1
    else if (!o.vert) {
      const r = sec.getBoundingClientRect()
      p = clamp01((-r.top / Math.max(1, r.height - innerHeight)) * 1.08 - 0.02)
    } else {
      const r = o.svg.getBoundingClientRect()
      p = clamp01((innerHeight * 0.72 - r.top) / r.height)
    }
    if (Math.abs(p - lp) < 0.0004) return
    lp = p
    const d = p * o.len
    const pt = o.main.getPointAtLength(d)
    const pt2 = o.main.getPointAtLength(Math.min(o.len, d + 1))
    const ang = (Math.atan2(pt2.y - pt.y, pt2.x - pt.x) * 180) / Math.PI
    o.car.setAttribute(
      "transform",
      `translate(${pt.x.toFixed(1)} ${pt.y.toFixed(1)}) rotate(${ang.toFixed(1)})`,
    )
    o.trail.style.strokeDashoffset = (o.len - d).toFixed(1)
    for (const s of o.stops) s.el.classList.toggle("on", p >= s.at)
    sec.classList.toggle("arrived", p >= 0.985)
  })
}
